import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Menu,
  MenuItem,
  Stack,
  Divider,
  Tooltip,
  Dialog,
  Checkbox,
} from "@mui/material";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import ContentPasteGoOutlinedIcon from "@mui/icons-material/ContentPasteGoOutlined";
import { Delete } from "@mui/icons-material";
import { displayColumn, columnCenter, rowSB } from "../../GlobalStyles";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import { RootState } from "../../store/store";
import apiRequest from "../../request/apiRequest/apiRequest";
import { allApis } from "../../request/config";
import { showSnack } from "../../store/slices/constants";
import CustomSearch, { fuzzySearch } from "../../Components/CustomSearch";
import CustomTextField from "../../Components/CustomTextfield";
import { allStatus } from "../../store/slices/constants/staticContents";
import AddSubtopicsDialog from "./AddSubtopicsDialog";
import Actions from "./Actions";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import Preview from "./Preview";
import GlobalLoader from "../../Components/GlobalLoader";
import { convertFirstUpper } from "../../Regex";
import NodeItem from "./NodeItem";
import {
  bgcolors,
  colors,
  formatNestedList,
  getNextPath,
  getParentPath,
  Node,
  NodeType,
  serachFilterFunction,
  TypeChip,
} from "./Utils";
import FormComp from "./forms/SingleQuestionAdd/FormComp";
import PathOrderManagment from "../../Components/PathOrderManagment";
import { updateRefetch } from "../../store/slices/userManagement";
import AllForms from "./forms/AllForms";
import NotesModal from "./NotesModal";
type ContentMangementsProps = {
  setFilters: (filters: any) => void;
  onlyPreview?: boolean;
  selected?: Record<NodeType, string[]>;
  expandedMap: Record<NodeType, string | null>;
  setExpandedMap: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setPagination: React.Dispatch<React.SetStateAction<Record<number, any>>>;
  pagination: Record<NodeType, number | null>;
};
export default function ContentMangements({
  setFilters,
  onlyPreview,
  selected,
  expandedMap,
  setExpandedMap,
  setPagination,
  pagination,
}: ContentMangementsProps) {
  const { masters, loading } = useAppSelecter(
    (state: RootState) => state.detailedMaster
  );

  const [preview, setPreview] = useState<false | "delete">(false);
  const [confirimation, setComfirmation] = useState<{
    open: boolean;
    type: false | "delete" | "status";
    node: any;
    newStatus?: string;
    parent?: string;
    ids?: string[];
  }>({ open: false, type: false, node: {} as Node });
  const [fullscreen, setfullscreen] = useState<any>(false);
  useEffect(() => {
    const url = new URL(window.location.href);
    const fullscreenParam = url.searchParams.get("fullscreen");

    if (fullscreenParam) {
      // Set the state
      setfullscreen(fullscreenParam);

      // Remove the param from the URL (no reload)
      url.searchParams.delete("fullscreen");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);
  const [filter_by, setFilter] = useState<any>({
    status: {
      EXAM: "all",
      GOAL: "all",
      SECTION: "all",
      UNIT: "all",
      TOPIC: "all",
      SUBTOPIC: "all",
      CLUSTER: "all",
      QUESTION: "all",
    },
    search: {
      EXAM: "",
      GOAL: "",
      SECTION: "",
      UNIT: "",
      TOPIC: "",
      SUBTOPIC: "",
      CLUSTER: "",
      QUESTION: "",
    },
  });
  const [notesOpen, setNotesOpen] = useState<any>(false);
  // .filter((s)=>filter_by.status[level]===s.status)
  const [onUpdate, setUpdate] = useState<any>(null);
  const [add, setAdd] = useState<any>(false);
  const [treeData, setTreeData] = useState<Node[]>([]);
  const [selectedItems, setSelectedItems] = useState<any>(selected ?? {});
  // expandedMap keeps track of which child is expanded for each parentKey.
  // e.g. { "__ROOT__": "exam2", "exam2": "goal1" }

  useEffect(() => {
    setFilters(expandedMap);
  }, [expandedMap]);

  useEffect(() => {
    if (masters.exams) {
      if (
        JSON.stringify(
          formatNestedList(treeData.map((s) => s.details) ?? [], "EXAM")
        ) !== JSON.stringify(formatNestedList(masters.exams ?? [], "EXAM"))
      ) {
        setTreeData(formatNestedList(masters.exams ?? [], "EXAM"));
      }
    }
  }, [masters.exams, treeData]);

  function updateChildrenByParentId(
    nodes: Node[],
    parentId: string | null,
    newChildren: Node[],
    TYPE: NodeType
  ): Node[] {
    return parentId
      ? nodes.map((node) => {
          // If the node matches the parentId, replace its children
          if (node.id === parentId && TYPE === (node.type ? node.type : TYPE)) {
            return { ...node, children: newChildren };
          }

          // Otherwise, recurse into children if they exist
          if (node.children && node.children.length > 0 && parentId) {
            return {
              ...node,
              children: updateChildrenByParentId(
                node.children,
                parentId,
                newChildren,
                TYPE
              ),
            };
          }
          // No match, return node as-is
          return node;
        })
      : newChildren;
  }
  useEffect(() => {
    if (masters.goals) {
      if (
        expandedMap.EXAM &&
        JSON.stringify(
          formatNestedList(
            treeData
              .find((s) => s.id === expandedMap.EXAM)
              ?.children?.map((s) => s.details) ?? [],
            "GOAL"
          )
        ) !== JSON.stringify(formatNestedList(masters.goals ?? [], "GOAL"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.EXAM ?? "", // parent node ID
            formatNestedList(masters.goals ?? [], "GOAL"),
            "EXAM"
          )
        );
      }
    }
  }, [masters.goals, treeData]);
  useEffect(() => {
    if (masters.sections) {
      if (
        expandedMap.GOAL &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.EXAM)?.children ?? [])
              .find((s) => s.id === expandedMap.GOAL)
              ?.children?.map((s) => s.details) ?? [],
            "SECTION"
          )
        ) !==
          JSON.stringify(formatNestedList(masters.sections ?? [], "SECTION"))
      ) {
        setTreeData((p) => {
          return updateChildrenByParentId(
            treeData,
            expandedMap.GOAL ?? "", // parent node ID
            formatNestedList(masters.sections ?? [], "SECTION"),
            "GOAL"
          );
        });
      }
    }
  }, [masters.sections, treeData]);
  useEffect(() => {
    if (masters.units) {
      if (
        expandedMap.SECTION &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.EXAM)?.children ?? [])
              .find((s) => s.id === expandedMap.GOAL)
              ?.children?.find((s) => s.id === expandedMap.SECTION)
              ?.children?.map((s) => s.details) ?? [],
            "UNIT"
          )
        ) !== JSON.stringify(formatNestedList(masters.units ?? [], "UNIT"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.SECTION ?? "", // parent node ID
            formatNestedList(masters.units ?? [], "UNIT"),
            "SECTION"
          )
        );
      }
    }
  }, [masters.units, treeData]);
  useEffect(() => {
    if (masters.topics) {
      if (
        expandedMap.UNIT &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.EXAM)?.children ?? [])
              .find((s) => s.id === expandedMap.GOAL)
              ?.children?.find((s) => s.id === expandedMap.SECTION)
              ?.children?.find((s) => s.id === expandedMap.UNIT)
              ?.children?.map((s) => s.details) ?? [],
            "TOPIC"
          )
        ) !== JSON.stringify(formatNestedList(masters.topics ?? [], "TOPIC"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.UNIT ?? "", // parent node ID
            formatNestedList(masters.topics ?? [], "TOPIC"),
            "UNIT"
          )
        );
      }
    }
  }, [masters.topics]);
  useEffect(() => {
    if (masters.subtopics) {
      if (
        expandedMap.TOPIC &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.EXAM)?.children ?? [])
              .find((s) => s.id === expandedMap.GOAL)
              ?.children?.find((s) => s.id === expandedMap.SECTION)
              ?.children?.find((s) => s.id === expandedMap.UNIT)
              ?.children?.find((s) => s.id === expandedMap.TOPIC)
              ?.children?.map((s) => s.details) ?? [],
            "SUBTOPIC"
          )
        ) !==
          JSON.stringify(formatNestedList(masters.subtopics ?? [], "SUBTOPIC"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.TOPIC ?? "", // parent node ID
            formatNestedList(masters.subtopics ?? [], "SUBTOPIC"),
            "TOPIC"
          )
        );
      }
    }
  }, [masters.subtopics, treeData]);
  useEffect(() => {
    if (masters.clusters) {
      if (
        expandedMap.SUBTOPIC &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.EXAM)?.children ?? [])
              .find((s) => s.id === expandedMap.GOAL)
              ?.children?.find((s) => s.id === expandedMap.SECTION)
              ?.children?.find((s) => s.id === expandedMap.UNIT)
              ?.children?.find((s) => s.id === expandedMap.TOPIC)
              ?.children?.find((s) => s.id === expandedMap.SUBTOPIC)
              ?.children?.map((s) => s.details) ?? [],
            "CLUSTER"
          )
        ) !==
          JSON.stringify(formatNestedList(masters.clusters ?? [], "CLUSTER"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.SUBTOPIC ?? "", // parent node ID
            formatNestedList(masters.clusters ?? [], "CLUSTER"),
            "SUBTOPIC"
          )
        );
      }
    }
  }, [masters.clusters, treeData]);
  useEffect(() => {
    if (masters.questions) {
      if (
        expandedMap.CLUSTER &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.EXAM)?.children ?? [])
              .find((s) => s.id === expandedMap.GOAL)
              ?.children?.find((s) => s.id === expandedMap.SECTION)
              ?.children?.find((s) => s.id === expandedMap.UNIT)
              ?.children?.find((s) => s.id === expandedMap.TOPIC)
              ?.children?.find((s) => s.id === expandedMap.SUBTOPIC)
              ?.children?.find((s) => s.id === expandedMap.CLUSTER)
              ?.children?.map((s) => s.details) ?? [],
            "QUESTION"
          )
        ) !==
          JSON.stringify(formatNestedList(masters.questions ?? [], "QUESTION"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.CLUSTER ?? "", // parent node ID
            formatNestedList((masters.questions ?? []) as any, "QUESTION"),
            "CLUSTER"
          )
        );
      }
    }
  }, [masters.questions, treeData]);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    nodeId: string | null;
  } | null>(null);

  const handleToggleSelect = (id: string, level: NodeType | "", all?: any) => {
    setSelectedItems((prev: { [x: string]: string[] }) => {
      const allChildsids = all ? all.map((s: { id: any }) => s.id) : [];
      const nextKey = level ? getNextPath(level) : "EXAM";

      return all
        ? {
            ...prev,
            [nextKey]:
              prev[nextKey] && prev[nextKey].length
                ? allChildsids.every((id: string) => prev[nextKey].includes(id))
                  ? [] // if all already selected, deselect all
                  : allChildsids // otherwise select all
                : allChildsids,
          }
        : {
            ...prev,
            [level]:
              prev[level] && prev[level].length
                ? prev[level].includes(id)
                  ? prev[level].filter((x) => x !== id) // toggle off
                  : [...prev[level], id] // toggle on
                : [id],
          };
    });
  };
  const dispatch = useAppDispatch();
  const [cut, setcut] = useState(false);
  const [loader, setLoader] = useState(false);
  const handleCloseContext = () => setContextMenu(null);

  const handleCopy = async (node: Node, only?: any) => {
    const selected = only
      ? node.children?.map((s) => s.id)
      : selectedItems[getNextPath(node.type)] ?? [];
    const saveArray =
      node?.children
        ?.filter((s: any) => selected.includes(s.id))
        .map((s: any) => ({
          id: s.id,
          text: s.details.text ?? s.label,
        })) ?? [];
    let existing: any[] = [];
    // const stored = localStorage.getItem("copied");
    // if (stored) {
    //   try {
    //     const decoded = atob(decodeURIComponent(escape(stored))); // decode base64 → JSON string
    //     existing = JSON.parse(decoded);
    //   } catch (e) {
    //     console.warn("Invalid base64 or JSON in localStorage:", e);
    //   }
    // }

    // // 🟢 Check if same combination already exists

    existing.push({
      parent: node
        ? { type: node.type, id: node.id, label: node.label }
        : { type: "EXAM" },
      childs: saveArray,
    });

    // 🟢 Encode before saving
    const encoded = btoa(
      unescape(encodeURIComponent(JSON.stringify(existing)))
    );
    localStorage.setItem("copied", encoded);
    setTimeout(() => {
      dispatch(updateRefetch(true));
    }, 1);
    // } else {
    //   console.warn("Some nodes not found:", { series, mocktest, section });
    // }
    // }
  };
  const updateStatusapiCall = async () => {
    setLoader(true);
    const types = confirimation.node.type;
    const statusResponse: any = await apiRequest({
      api:
        types === "QUESTION"
          ? allApis.question_cluster_bulk_update
          : allApis.statusUpdate,
      payload: {
        level:
          types === "EXAM"
            ? "exam"
            : types === "GOAL"
            ? "goal"
            : types === "SECTION"
            ? "section"
            : types === "UNIT"
            ? "unit"
            : types === "TOPIC"
            ? "topic"
            : types === "SUBTOPIC"
            ? "subtopic"
            : types === "CLUSTER"
            ? "cluster"
            : types === "QUESTION"
            ? "question"
            : "",
        ids: confirimation.node.id
          ? [confirimation.node.id]
          : selectedItems[confirimation.node.type] ?? [],
        status: confirimation.newStatus,
      },
      method: "PUT",
    });
    if (statusResponse && statusResponse.error) {
      setLoader(false);
      dispatch(
        showSnack({
          open: true,
          severity: "error",
          message: statusResponse.msg,
        })
      );
    } else {
      setFilters((p: Record<string, any>) => {
        return { ...p, [`${types}refetch`]: !p[`${types}refetch`] };
      });
      dispatch(
        showSnack({
          open: true,
          severity: "success",
          message: confirimation.node.id
            ? `${types} status updated successfully`
            : `${
                (selectedItems[types] ?? []).length
              } ${types}s status updated successfully`,
        })
      );
      setLoader(false);
      setComfirmation({ open: false, type: false, node: {} as Node });
    }
  };
  const handleStatusSingle = async (
    node: Node | { type: NodeType },
    status: string
  ) => {
    setComfirmation({
      open: true,
      type: "status",
      node: node,
      newStatus: status,
    });
  };
  const handleDeleteSingle = async (node: any, parent?: any) => {
    setComfirmation({
      open: true,
      type: "delete",
      node: node,
      parent: parent,
    });
  };
  const handleDeleteSingleApi = async () => {
    const data = confirimation.node.type;
    setLoader(true);
    let statusResponse: any;
    if (data === "QUESTION") {
      statusResponse = await apiRequest({
        api: allApis.question_cluster_bulk_update,
        payload: {
          level:
            data === "EXAM"
              ? "exam"
              : data === "GOAL"
              ? "goal"
              : data === "SECTION"
              ? "section"
              : data === "UNIT"
              ? "unit"
              : data === "TOPIC"
              ? "topic"
              : data === "SUBTOPIC"
              ? "subtopic"
              : data === "QUESTION"
              ? "question"
              : "",
          ids: confirimation.node.id
            ? [confirimation.node.id]
            : selectedItems[confirimation.node.type] ?? [],
          delete: true,
        },
        method: "DELETE",
      });
    } else if (data !== "CLUSTER") {
      statusResponse = await apiRequest({
        api: allApis.delete,
        payload: {
          level:
            data === "EXAM"
              ? "exam"
              : data === "GOAL"
              ? "goal"
              : data === "SECTION"
              ? "section"
              : data === "UNIT"
              ? "unit"
              : data === "TOPIC"
              ? "topic"
              : data === "SUBTOPIC"
              ? "subtopic"
              : data === "QUESTION"
              ? "question"
              : "",
          ids: confirimation.node.id
            ? [confirimation.node.id]
            : selectedItems[confirimation.node.type] ?? [],
        },
        method: "DELETE",
      });
    } else {
      statusResponse = await apiRequest({
        api: allApis.delete_cluster,
        payload: {
          node_id: confirimation.parent ?? "",
          cluster_ids: confirimation.node.id
            ? [confirimation.node.id]
            : selectedItems[data] ?? [],
        },
        method: "DELETE",
      });
    }
    if (statusResponse && statusResponse.error) {
      setLoader(false);
      dispatch(
        showSnack({
          open: true,
          severity: "error",
          message: statusResponse.msg,
        })
      );
    } else {
      setFilters((p: Record<string, any>) => {
        return { ...p, [`${data}refetch`]: !p[`${data}refetch`] };
      });
      setSelectedItems((p: any) => {
        return { ...p, [data]: [] };
      });
      setLoader(false);

      dispatch(
        showSnack({
          open: true,
          severity: "success",
          message: confirimation.node.id
            ? `${convertFirstUpper(data)} deleted successfully`
            : `${
                (selectedItems[data] ?? []).length
              } ${data}s deleted successfully`,
        })
      );
      setComfirmation({ open: false, type: false, node: {} as Node });
    }
  };
  const handleToggleExpand = (
    type: NodeType,
    nodeId: string,
    parent: string
  ) => {
    setExpandedMap((prev: { [x: string]: any }) => {
      const prevSelected = prev[type];
      const newMap = { ...prev };
      if (type === "EXAM") {
        newMap["GOAL"] = null;
        newMap["SECTION"] = null;
        newMap["UNIT"] = null;
        newMap["TOPIC"] = null;
        newMap["SUBTOPIC"] = null;
        newMap["CLUSTER"] = null;
        newMap["QUESTION"] = null;
      } else if (type === "GOAL") {
        newMap["SECTION"] = null;
        newMap["UNIT"] = null;
        newMap["TOPIC"] = null;
        newMap["SUBTOPIC"] = null;
        newMap["CLUSTER"] = null;
        newMap["QUESTION"] = null;
      } else if (type === "SECTION") {
        newMap["UNIT"] = null;
        newMap["TOPIC"] = null;
        newMap["SUBTOPIC"] = null;
        newMap["CLUSTER"] = null;
        newMap["QUESTION"] = null;
      } else if (type === "UNIT") {
        newMap["TOPIC"] = null;
        newMap["SUBTOPIC"] = null;
        newMap["CLUSTER"] = null;
        newMap["QUESTION"] = null;
      } else if (type === "TOPIC") {
        newMap["SUBTOPIC"] = null;
        newMap["CLUSTER"] = null;
        newMap["QUESTION"] = null;
      } else if (type === "SUBTOPIC") {
        newMap["CLUSTER"] = null;
        newMap["QUESTION"] = null;
      } else if (type === "CLUSTER") {
        newMap["QUESTION"] = null;
      }
      // If toggling off the currently selected child
      if (prevSelected === nodeId) {
        newMap[type] = null;
        return newMap;
      }
      newMap[type] = nodeId;
      return newMap;
    });
    setPagination((prev) => {
      return {
        ...prev,
        [getNextPath(type)]: 1,
      };
    });
    setSelectedItems((p: any) => {
      return { ...p, [getNextPath(type)]: [] };
    });
  };
  const getFilteredList = (status: any, level: string, list?: any) => {
    return serachFilterFunction(
      ((list ?? []) as Node[]).filter((s) =>
        status === "all" ? true : status === s.status
      ),
      filter_by.search[level] ?? ""
    ).length;
  };

  const headerDetail = ({
    node,
    parentId,
    onToggleExpand,
    plist,
  }: {
    node?: Node | null;
    parentId?: string | null;
    onToggleExpand: (type: NodeType, nodeId: string, parent: string) => void;
    plist?: Node[];
  }) => {
    const nodeType = node ? node.type : "EXAM";
    const childType = node
      ? node?.children?.[0]?.type ?? getNextPath(node.type)
      : "EXAM";
    const showExpandIcon = !expandedMap[nodeType];
    const allChild = serachFilterFunction(
      ((node?.children ?? treeData) as Node[]).filter((s) =>
        filter_by.status[childType] === "all"
          ? true
          : filter_by.status[childType] === s.status
      ),
      filter_by.search[childType] ?? ""
    ) as Node[];
    const allChildsids = allChild.map((s: { id: any }) => s.id);
    const allSelected = selectedItems[childType]
      ? allChildsids.every((id: string) =>
          selectedItems[childType].includes(id)
        )
      : false;
    //   (selectedItems[childType] ?? []).length ===
    //  allChild.length;
    const isExpanded = expandedMap[nodeType] === (node?.id ?? "");
    return (
      <Box
        sx={{
          ...rowSB,
          p: 2,
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "background.paper",
          //  isExpanded ? "action.selected" : "background.paper",
          borderLeft: (theme) =>
            `4px solid ${
              isExpanded
                ? colors[node?.type ?? "EXAM"]
                  ? colors[node?.type ?? "EXAM"]
                  : theme.palette.primary.main
                : "transparent"
            }`,
          background: isExpanded
            ? bgcolors[node?.type ?? "EXAM"]
              ? bgcolors[node?.type ?? "EXAM"]
              : ""
            : "",
          transition: "all 0.2s ease",
        }}
      >
        <Stack direction="row" alignItems="center" spacing="8px">
          {/* {preview && (
            <ConfirmationDialog
              open={preview === "delete"}
              title={
                preview === "delete"
                  ? "Are you sure you want to delete following content?"
                  : "Selected Content Preview"
              }
              sx={{
                "& .MuiDialog-container": {
                  backdropFilter: "blur(3px)", // subtle blur
                },
                "& .MuiDialog-paper": {
                  margin: "0",
                  minHeight: "100dvh",
                  minWidth: "100%",
                  borderRadius: 0,
                },
              }}
              onClose={() => {
                setPreview(false);
              }}
              onSubmit={() => {
                if (preview === "delete") {
                  handleDeleteSelected(node ? childType : "EXAM", node);
                }
              }}
              content={<Preview selected={selectedItems} />}
            />
          )} */}

          <TypeChip type={childType} />
          <Checkbox
            size="small"
            checked={allSelected}
            // disabled={!showExpandicon?true:isExpanded}
            onClick={(e) => {
              e.stopPropagation();

              handleToggleSelect(
                node ? node.id : "",
                node?.type ?? "",
                allChild
              );
            }}
            sx={{ ml: 0.5 }}
          />
          {(selectedItems[childType] ?? []).length > 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1, minWidth: 80 }}
            >
              {(selectedItems[childType] ?? []).length} selected
            </Typography>
          ) : (
            ""
          )}
          <CustomTextField
            autoComplete="off"
            value={
              filter_by.status[childType]
                ? `${filter_by.status[childType]}`
                : ""
            }
            select
            variant="outlined"
            size="small"
            sx={{
              width: "130px",
              "& .MuiInputBase-root": { padding: "7px !important" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderRadius: "4px !important" },
              },
              "& .MuiInputBase-input": {
                textTransform: "capitalize",
                padding: "0 0 0 5px !important",
              },
            }}
            onChange={(e) => {
              setSelectedItems((p: any) => {
                return { ...p, [childType]: [] };
              });
              setFilter((p: any) => {
                return {
                  ...p,
                  status: { ...p.status, [childType]: e.target.value },
                };
              });
            }}
          >
            {allStatus?.map(({ label }: any, i: number) => (
              <MenuItem
                key={i}
                value={label}
                sx={{ textTransform: "capitalize" }}
              >
                {`${label} ${
                  getFilteredList(
                    label,
                    childType,
                    node ? node?.children : treeData
                  ) > 0
                    ? `(${getFilteredList(
                        label,
                        childType,
                        node ? node?.children : treeData
                      )})`
                    : ""
                }`}
              </MenuItem>
            ))}
          </CustomTextField>
          <CustomSearch
            placeholder="Search by name or id"
            copyData={node ? node.children : treeData}
            fieldName={["name", "id"]}
            value={filter_by.search[childType] ?? ""}
            // filteredData={(array: ObjectValues[]) => {}}
            onClear={() => {}}
            onChange={(val) => {
              setFilter((p: any) => {
                return {
                  ...p,
                  search: { ...p.search, [childType]: val },
                };
              });
            }}
            sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderRadius: "4px !important" },
              },
            }}
          />
        </Stack>

        {/* Right section: Actions */}
        <Box sx={{ display: "flex", gap: "10px" }}>
          {" "}
          {onlyPreview ? (
            ""
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              {(selectedItems[childType] ?? selectedItems["EXAM"] ?? [])
                .length !== 0 ? (
                <Actions
                  onReFetch={(type: NodeType) => {
                    setSelectedItems((p: any) => {
                      return { ...p, [type]: [], [getNextPath(type)]: [] };
                    });
                    setFilters((p: Record<string, any>) => {
                      return { ...p, [`${type}refetch`]: !p[`${type}refetch`] };
                    });
                  }}
                  disabled={(selectedItems[childType] ?? []).length === 0}
                  type={node ? childType : "EXAM"}
                  row={
                    filter_by.status[childType] === "all"
                      ? null
                      : { status: filter_by.status[childType] }
                  }
                  onCut={() => {
                    if (node) {
                      handleCopy(node);
                    }
                  }}
                  setLoader={setLoader}
                  handlePaste={() => {
                    setFilters((p: Record<string, any>) => {
                      return {
                        ...p,
                        [`${node?.type}refetch`]: !p[`${node?.type}refetch`],
                      };
                    });
                  }}
                  onDelete={
                    () => {
                      handleDeleteSingle(
                        node
                          ? { type: node ? childType : "EXAM" }
                          : { type: "EXAM" },
                        node?.id ?? ""
                      );
                    }
                    // handleDeleteSelected(node ? childType : "EXAM", node)
                  }
                  onStatus={(status: any) => {
                    handleStatusSingle(
                      { type: node ? childType : "EXAM" },
                      status
                    );
                    // }
                    //   handleStatusUpdate()
                  }}
                />
              ) : (
                ""
              )}
              {getNextPath(node?.type ?? "EXAM") === "QUESTION" ||
              getNextPath(node?.type ?? "EXAM") === "CLUSTER" ? (
                ""
              ) : (
                <PathOrderManagment
                  modal={
                    getNextPath(node?.type ?? "EXAM") === "GOAL"
                      ? "goal"
                      : "syllabus_tree_node"
                  }
                  list={
                    node?.children ? node?.children.map((s) => s.details) : []
                  }
                  title={
                    convertFirstUpper(
                      getNextPath(node?.type ?? "EXAM").toLowerCase()
                    ) as string
                  }
                  onClear={() => {
                    setFilters((p: Record<string, any>) => {
                      return {
                        ...p,
                        [`${getNextPath(node?.type ?? "EXAM")}refetch`]:
                          !p[`${getNextPath(node?.type ?? "EXAM")}refetch`],
                      };
                    });
                    dispatch(
                      showSnack({
                        open: true,
                        severity: "success",
                        message: `Path order of all the ${convertFirstUpper(
                          getNextPath(node?.type ?? "EXAM").toLowerCase()
                        )}s has been successfully updated.`,
                      })
                    );
                  }}
                />
              )}
              {add &&
                (node
                  ? node &&
                    node.id === add.id &&
                    (node.type === "EXAM" ? true : plist)
                  : true) && (
                  <Dialog fullScreen={true} open={Boolean(add)}>
                    <AllForms
                      dockedCard={{
                        title: `${childType.toLowerCase()}s`,
                        edit: false,
                        selected_row: null as any,
                      }}
                      getGoalId={expandedMap.GOAL ?? ""}
                      selectedExam={
                        node && plist
                          ? {
                              selected_row: node.id
                                ? plist.find((s: Node) => s.id === node.id)
                                    ?.details
                                : null,
                              list: plist.map((s: Node) => s.details),
                              selected_row_field: "name",
                            }
                          : {}
                      }
                      onClose={() => {
                        setAdd(false);
                      }}
                      onSubmit={() => {
                        setFilters((p: Record<string, any>) => {
                          return {
                            ...p,
                            [`${childType}refetch`]: !p[`${childType}refetch`],
                          };
                        });
                      }}
                    />
                  </Dialog>
                )}
              <AddSubtopicsDialog
                setAdd={() => {
                  setAdd(node ?? true);
                }}
                text={childType}
                onSubmit={() => {
                  setFilters((p: Record<string, any>) => {
                    return {
                      ...p,
                      [`${childType}refetch`]: !p[`${childType}refetch`],
                    };
                  });
                }}
                parent_name={node?.label ?? ""}
                parent={
                  node
                    ? {
                        [`parent_${node.type?.toLowerCase() ?? "exam"}_id`]:
                          node.id,
                      }
                    : {}
                }
              />
            </Stack>
          )}
          <Tooltip
            children={
              <Box
                sx={{
                  cursor: "pointer",
                  padding: "1px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => {
                  if (
                    fullscreen ===
                    (node
                      ? `parent-${nodeType}-${node.id}`
                      : `parent-${nodeType}`)
                  ) {
                    setfullscreen(false);
                  } else {
                    const url = new URL(window.location.href);
                    url.searchParams.set(
                      "fullscreen",
                      node
                        ? `parent-${nodeType}-${node.id}`
                        : `parent-${nodeType}`
                    );

                    // Open the updated URL in a new tab
                    window.open(url.toString(), "_blank");
                  }

                  if (parentId && node && showExpandIcon) {
                    onToggleExpand(node.type, node.id, parentId);
                  }
                }}
              >
                {fullscreen ===
                (node
                  ? `parent-${nodeType}-${node.id}`
                  : `parent-${nodeType}`) ? (
                  <FullscreenExitIcon />
                ) : (
                  <FullscreenIcon />
                )}
              </Box>
            }
            sx={{ zIndex: 9999999 }}
            title={
              fullscreen ===
              (node ? `parent-${nodeType}-${node.id}` : `parent-${nodeType}`)
                ? "Exit Fullscreen mode"
                : "Enter Fullscreen mode"
            }
          />
        </Box>
      </Box>
    );
  };

  const isFullscreen = fullscreen === `parent-EXAM`;
  const pChild = (
    <>
      {confirimation.open && (
        <ConfirmationDialog
          open={confirimation.open}
          title={"Are you sure you want to"}
          sx={
            {
              // "& .MuiDialog-container": {
              //   backdropFilter: "blur(3px)", // subtle blur
              // },
              // "& .MuiDialog-paper": {
              //   margin: "0",
              //   minHeight: "100dvh",
              //   minWidth: "100%",
              //   borderRadius: 0,
              // },
            }
          }
          onClose={() => {
            setComfirmation({ open: false, type: false, node: {} as Node });
          }}
          onSubmit={() => {
            if (confirimation.type === "status") {
              updateStatusapiCall();
            } else if (confirimation.type === "delete") {
              handleDeleteSingleApi();
            }
            {
            }
          }}
          content={
            <>
              {confirimation.type === "delete"
                ? `delete ${
                    confirimation.node?.label
                      ? confirimation.node?.label
                      : `${
                          (selectedItems[confirimation.node.type] ?? []).length
                        } selected`
                  } ${confirimation.node.type}${
                    (selectedItems[confirimation.node.type] ?? []).length > 1
                      ? "s"
                      : ""
                  } ?`
                : `change status of ${
                    confirimation.node?.label
                      ? confirimation.node?.label
                      : `${
                          (selectedItems[confirimation.node.type] ?? []).length
                        } selected`
                  } ${confirimation.node.type}${
                    (selectedItems[confirimation.node.type] ?? []).length > 1
                      ? "s"
                      : ""
                  } to ${convertFirstUpper(confirimation.newStatus ?? "")}`}
            </>
          }
        />
      )}
      {(loader || loading.exams) && <GlobalLoader open={true} />}
      {headerDetail({ onToggleExpand: handleToggleExpand })}
      <Box
        sx={{
          p: "10px",
          flex: 8,
          overflowY: "auto",
          boxSizing: "border-box",
          background: "#F4F5FA",
        }}
        className="scrollBluePrimarythin"
        id="treeScrollContainer"
      >
        {(
          serachFilterFunction(
            treeData.filter((s) =>
              filter_by.status["EXAM"] === "all"
                ? true
                : filter_by.status["EXAM"] === s.status
            ),
            filter_by.search["EXAM"] ?? ""
          ) as Node[]
        ).filter((s) =>
          onlyPreview ? (selectedItems[s.type] ?? []).includes(s.id) : true
        ).length > 0 ? (
          (
            serachFilterFunction(
              treeData.filter((s) =>
                filter_by.status["EXAM"] === "all"
                  ? true
                  : filter_by.status["EXAM"] === s.status
              ),
              filter_by.search["EXAM"] ?? ""
            ) as Node[]
          )
            .filter((s) =>
              onlyPreview ? (selectedItems[s.type] ?? []).includes(s.id) : true
            )
            .map((node) => (
              <NodeItem
                plist={treeData}
                setNotesOpen={setNotesOpen}
                key={node.id}
                onCut={(data: Node) => {
                  handleCopy(data, "only");
                }}
                onEdit={() => {
                  setAdd(node ?? true);
                }}
                setLoader={setLoader}
                setPagination={setPagination}
                pagination={pagination}
                setUpdate={setUpdate}
                onReFetch={(type: NodeType) => {
                  setSelectedItems((p: any) => {
                    return { ...p, [type]: [], [getNextPath(type)]: [] };
                  });
                  setFilters((p: Record<string, any>) => {
                    return { ...p, [`${type}refetch`]: !p[`${type}refetch`] };
                  });
                }}
                update={onUpdate}
                node={node}
                handleDeleteSingle={handleDeleteSingle}
                handleStatusSingle={handleStatusSingle}
                depth={0}
                hierarchy={{
                  exam: node.label,
                  goal: "",
                  section: "",
                  unit: "",
                  topic: "",
                  subtopic: "",
                  cluster: "",
                  question: "",
                  exam_id: node.id,
                  goal_id: "",
                  section_id: "",
                  unit_id: "",
                  topic_id: "",
                  subtopic_id: "",
                  cluster_id: "",
                  question_id: "",
                }}
                selectAll={false}
                fullscreen={fullscreen}
                setfullscreen={setfullscreen}
                parentId={null}
                cut={cut}
                onlyPreview={onlyPreview}
                getFilteredList={(list: Node[], type: NodeType) => {
                  return serachFilterFunction(
                    list.filter((s) =>
                      filter_by.status[type] === "all"
                        ? true
                        : filter_by.status[type] === s.status
                    ),
                    filter_by.search[type] ?? ""
                  ) as Node[];
                }}
                headerDetail={headerDetail}
                selectedItems={selectedItems}
                onToggleSelect={handleToggleSelect}
                expandedMap={expandedMap}
                onToggleExpand={handleToggleExpand}
              />
            ))
        ) : (
          <Box sx={{ width: "100%", height: "100%", ...columnCenter }}>
            No exams found
          </Box>
        )}
      </Box>
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContext}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            handleCloseContext();
          }}
        >
          Cut <ContentCutIcon fontSize="small" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseContext();
          }}
        >
          copy <CopyAllIcon fontSize="small" />
        </MenuItem>{" "}
        <MenuItem
          onClick={() => {
            handleCloseContext();
          }}
        >
          paste <ContentPasteGoOutlinedIcon fontSize="small" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const id = contextMenu?.nodeId;
            if (id) {
              const deleteById = (nodes: Node[]): Node[] =>
                nodes
                  .filter((n) => n.id !== id)
                  .map((n) => ({
                    ...n,
                    children: n.children ? deleteById(n.children) : undefined,
                  }));
              setTreeData((prev) => deleteById(prev));
            }
            handleCloseContext();
          }}
        >
          Delete <Delete fontSize="small" />
        </MenuItem>
      </Menu>
    </>
  );

  return isFullscreen ? (
    <Dialog open={true} fullScreen={true} TransitionProps={{ timeout: 400 }}>
      {pChild}
    </Dialog>
  ) : (
    <Paper
      sx={{
        flexGrow: 8,
        m: "0 4px 4px",
        ...displayColumn,
        height: isFullscreen ? "100dvh" : "calc(100% - 4px)",
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        zIndex: isFullscreen ? 9999 : "auto",
        minWidth: isFullscreen ? "100%" : "auto",
        transition: "all 0.4s ease",
      }}
      id={`parent-EXAM`}
    >
      {notesOpen.node && (
        <NotesModal
          open={Boolean(notesOpen.node)}
          row={notesOpen.node}
          onClose={() => setNotesOpen(false)}
          onReFetch={(type: NodeType) => {
            setSelectedItems((p: any) => {
              return { ...p, [type]: [], [getNextPath(type)]: [] };
            });
            setFilters((p: Record<string, any>) => {
              return { ...p, [`${type}refetch`]: !p[`${type}refetch`] };
            });
          }}
        />
      )}
      {onUpdate && onUpdate.node.id && (
        <Dialog open fullScreen>
          {onUpdate.node.type === "QUESTION" ? (
            <FormComp
              onClose={() => setUpdate(false)}
              onSubmit={(que: any) => {
                setUpdate(false);
              }}
              filters={{
                exam: onUpdate.hierarchy.exam,
                goal: onUpdate.hierarchy.goal,
                section: onUpdate.hierarchy.section,
                unit: onUpdate.hierarchy.unit,
                topic: onUpdate.hierarchy.topic,
                subtopic: onUpdate.hierarchy.subtopic,
                cluster: {
                  name: onUpdate.hierarchy.cluster,
                  id: onUpdate.parent,
                },
              }}
              question_id={onUpdate.node.id}
            />
          ) : (
            <AllForms
              dockedCard={{
                title: `${onUpdate.node.type.toLowerCase()}s`,
                edit: true,
                selected_row: { ...onUpdate.node.details },
              }}
              getGoalId={expandedMap.GOAL ?? ""}
              selectedExam={
                onUpdate.node && onUpdate.parent
                  ? {
                      selected_row: onUpdate.hierarchy[
                        getParentPath(onUpdate.node.type).toLowerCase()
                      ]
                        ? {
                            name: onUpdate.hierarchy[
                              getParentPath(onUpdate.node.type).toLowerCase()
                            ],
                            id: onUpdate.parent,
                            stc:
                              onUpdate.hierarchy[`parent_id_stcsubtopic`] ?? "",
                          }
                        : null,
                      list: onUpdate.hierarchy[
                        getParentPath(onUpdate.node.type).toLowerCase()
                      ]
                        ? [
                            {
                              name: onUpdate.hierarchy[
                                getParentPath(onUpdate.node.type).toLowerCase()
                              ],
                              id: onUpdate.parent,
                            },
                          ]
                        : [],
                      selected_row_field: "name",
                    }
                  : {}
              }
              onClose={() => {
                setAdd(false);
                setUpdate(false);
              }}
              onSubmit={() => {
                setFilters((p: Record<string, any>) => {
                  return {
                    ...p,
                    [`${onUpdate.node.type}refetch`]:
                      !p[`${onUpdate.node.type}refetch`],
                  };
                });
              }}
            />
          )}
        </Dialog>
      )}
      {pChild}
    </Paper>
  );
}
